import React, { useState } from 'react';

const TIERS = [
  { key: 'top', label: 'Top Notes', blurb: 'The first impression' },
  { key: 'middle', label: 'Middle Notes', blurb: 'The heart' },
  { key: 'base', label: 'Base Notes', blurb: 'The trail it leaves' }
];

// Ingredient still-life for a tier, e.g. /notes/ombre-nomade-top.webp
const noteImg = (slug, tier) => `/notes/${slug}-${tier}.webp`;

function NoteCard({ product, tier, label, blurb }) {
  const [failed, setFailed] = useState(false);
  const names = product.notes[tier] || [];

  return (
    <div className="group text-center">
      <div
        className="relative aspect-square rounded-md overflow-hidden border border-[#E5DCC9] group-hover:border-[#C9A961] transition-colors duration-500"
        style={{
          background: `radial-gradient(120% 90% at 50% 15%, #FBF6EC 0%, #F0E6D3 100%)`
        }}
      >
        {!failed ? (
          <img
            src={noteImg(product.slug, tier)}
            alt={`${label} — ${names.join(', ')}`}
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
            className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700 ease-out select-none"
          />
        ) : (
          // Composed fallback until the still-life is added for this product:
          // one tinted disc per note, so the tile still reads as designed.
          <div className="w-full h-full flex flex-col items-center justify-center px-3 gap-3">
            <span className="flex items-end gap-2">
              {names.map((n, i) => (
                <span
                  key={n}
                  className="block rounded-full"
                  style={{
                    width: i === 0 ? 38 : 26,
                    height: i === 0 ? 38 : 26,
                    opacity: 0.9 - i * 0.18,
                    background: `radial-gradient(circle at 34% 28%, #FFFFFF88, ${product.accent})`,
                    boxShadow: `0 4px 12px ${product.accent}33`
                  }}
                />
              ))}
            </span>
            <span className="h-px w-8 bg-[#C9A961]/60" />
            <p className="font-serif-display text-base sm:text-lg leading-snug text-[#1A2332]/75">
              {names.join(' · ')}
            </p>
          </div>
        )}

        {/* gold corner ticks */}
        <span className="absolute top-2 left-2 w-3.5 h-3.5 border-t border-l border-[#C9A961] opacity-0 group-hover:opacity-100 transition-opacity" />
        <span className="absolute bottom-2 right-2 w-3.5 h-3.5 border-b border-r border-[#C9A961] opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      <p className="mt-3 text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-[#1A2332]/55">
        <span className="text-[#C9A961]">[</span> {label} <span className="text-[#C9A961]">]</span>
      </p>
      <p className="mt-1 font-serif-body text-sm sm:text-[15px] text-[#1A2332] leading-snug">
        {names.join(' • ')}
      </p>
      <p className="mt-0.5 font-serif-body italic text-[11px] text-[#1A2332]/45">{blurb}</p>
    </div>
  );
}

export default function FragranceNotes({ product }) {
  return (
    <div className="mb-6 sm:mb-7">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-px w-7 bg-[#C9A961]" />
        <p className="text-[11px] tracking-[0.3em] uppercase text-[#1A2332]/60">Fragrance Notes</p>
        <span className="h-px flex-1 bg-[#E5DCC9]" />
      </div>

      <div className="grid grid-cols-3 gap-3 sm:gap-5">
        {TIERS.map(t => (
          <NoteCard
            key={t.key}
            product={product}
            tier={t.key}
            label={t.label}
            blurb={t.blurb}
          />
        ))}
      </div>
    </div>
  );
}
